import CurrencyDropdown from '@/components/CommonComponents/CurrencyDropdown';
import CustomDropdown from '@/components/CommonComponents/CustomDropdown';
import DatePicker from '@/components/CommonComponents/DatePicker';
import MultiSelectDropdown from '@/components/CommonComponents/MultiSelectDropdown';
import DropdownWithRadio from '@/components/CommonComponents/RadioButtons';
import { validateEndDateWithSurveys } from '@/helper/utils';
import { StepValidatorRef, useStepValidation } from '@/hooks/useStepValidation';
import { AppDispatch, RootState } from '@/store';
import { getSitesByProjectId } from '@/store/slices/siteManagementSlice/siteManagementSlice';
import { Language } from '@/types/metaData';
import { ProjectDropdownDto } from '@/types/project.types';
import { SiteByProjectIdDto } from '@/types/site.types';
import moment from 'moment-timezone';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { surveysetCreationType } from './CreateSurveySet';

type BasicInfoProps = {
  projects: ProjectDropdownDto[];
  languages: Language[];
  sites: SiteByProjectIdDto[];
  loadingSites: boolean;
  isEditing?: boolean;
  lockedProjectId?: number;
};
const BasicInfo = forwardRef<StepValidatorRef, BasicInfoProps>(
  ({ isEditing, projects, languages, lockedProjectId }, ref) => {
    useStepValidation(ref, [
      'title',
      'numberOfSurveys',
      'projectId',
      'siteId',
      'languages',
      'currency',
      'rewardAmount',
      'claimType',
      'startDate',
      'endDate',
      'timezone',
    ]);
    const {
      register,
      control,
      setValue,
      formState: { errors },
      getValues,
    } = useFormContext<surveysetCreationType>();

    const dispatch = useDispatch<AppDispatch>();
    const projectId = useWatch({
      control,
      name: 'projectId',
    });
    const siteId = useWatch({
      control,
      name: 'siteId',
    });

    const timezone = useWatch({
      control,
      name: 'timezone',
    });

    const currency = useWatch({
      control,
      name: 'currency',
    });

    const prevProjectId = useRef<number | null>(null);
    const { sitesByProject, loadingSitesByProject } = useSelector(
      (state: RootState) => state.sites,
    );
    // const [selectedTimezone, setSelectedTimezone] = useState<string>('UTC');
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);
    const timezoneOptions = moment.tz.names();

    const projectOptions = projects.map((p) => ({ id: p.id, name: p.name }));

    useEffect(() => {
      if (!projectId || projectId === 0) return;

      if (prevProjectId.current === projectId) return;

      prevProjectId.current = projectId;
      dispatch(getSitesByProjectId(projectId));
    }, [projectId, dispatch, setValue]);

    useEffect(() => {
      if (!siteId || siteId === 0) return;

      const selectedSite = sitesByProject.find((site) => site.id === siteId);

      if (selectedSite?.currency && !isEditing && !currency) {
        setValue('currency', selectedSite.currency, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }

      // AUTO-FILL TIMEZONE FROM SITE GEOFENCE - always update when site changes
      const siteTimezone = selectedSite?.geofence?.timezone;

      if (siteTimezone) {
        setValue('timezone', siteTimezone, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    }, [siteId, sitesByProject, setValue, isEditing]);

    return (
      <div className="mx-auto text-[#621C43]">
        <div className="border-b border-b-[#621C43] px-5 py-6 flex flex-col">
          <h2 className="text-xl font-bold">
            {isEditing ? 'Basic information' : 'Basic information'}
          </h2>

          <p className="text-sm font-Lato font-medium text-[#621C43]">
            {isEditing
              ? 'Enter the basic details for the survey set'
              : 'Enter the basic details for the new survey set'}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-6 mt-2">
          <div>
            <label className="block text-sm font-medium mb-1 text-[#621C43]">
              Survey-Set Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              {...register('title', {
                required: 'Title is required',
                minLength: {
                  value: 5,
                  message: 'Survey-Set title must be 5-60 characters',
                },
                maxLength: {
                  value: 60,
                  message: 'Survey-Set title cannot exceed 60 characters',
                },
                validate: (v) => v.trim().length > 0 || 'Title cannot be empty spaces',
              })}
              className="w-full p-2 px-3 border-[#4A082D] rounded-lg bg-[#4A082D33] placeholder-[#621C43] text-[#4A082D] font-normal italic"
              placeholder="Enter survey title"
            />
            {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-[#621C43]">
              Includes how many surveys? <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              {...register('numberOfSurveys', {
                required: 'Number of surveys is required',
                valueAsNumber: true,
                min: {
                  value: 1,
                  message: 'Minimum 1 survey required',
                },
              })}
              className="w-full p-2 px-3 border-[#4A082D] rounded-lg bg-[#4A082D33] placeholder-[#621C43] text-[#4A082D] font-normal italic"
              placeholder="Enter number of surveys"
            />
            {errors.numberOfSurveys && (
              <p className="text-red-500 text-xs mt-1">{errors.numberOfSurveys.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-[#621C43]">
              Select Project <span className="text-red-500">*</span>
            </label>

            <Controller
              name="projectId"
              control={control}
              rules={{
                required: 'Project is required',
                validate: (v) => v > 0 || 'Please select a project',
              }}
              render={({ field }) => (
                <CustomDropdown
                  value={field.value || 0}
                  onChange={field.onChange}
                  options={[
                    { value: 0, label: 'Select project' },
                    ...projects.map((p) => ({ value: p.id, label: p.name })),
                  ]}
                  placeholder="Select project"
                  disabled={!!lockedProjectId}
                  className="bg-[#4A082D33]/10 rounded-lg text-base placeholder:text-base"
                />
              )}
            />

            {errors.projectId && (
              <p className="text-red-500 text-xs mt-1">{errors.projectId.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-[#621C43]">
              Select Site <span className="text-red-500">*</span>
            </label>
            <Controller
              name="siteId"
              control={control}
              rules={{
                required: 'Site is required',
                validate: (v) => v > 0 || 'Please select a site',
              }}
              render={({ field }) => (
                <CustomDropdown
                  value={field.value || 0}
                  onChange={(value) => {
                    field.onChange(value);
                    const selectedSite = sitesByProject.find((s) => s.id === value);
                    const tz = selectedSite?.geofence?.timezone;
                    if (tz) {
                      setValue('timezone', tz, { shouldValidate: true, shouldDirty: true });
                    }
                  }}
                  options={[
                    { value: 0, label: 'select site' },
                    ...sitesByProject.map((site) => ({ value: site.id, label: site.name })),
                  ]}
                  placeholder="Select site"
                  className="bg-[#4A082D33]/10 rounded-lg text-base"
                />
              )}
            />

            {errors.siteId && <p className="text-red-500 text-xs mt-1">{errors.siteId.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-[#621C43]">
              Language(s) <span className="text-red-500">*</span>
            </label>
            <Controller
              name="languages"
              control={control}
              rules={{
                validate: (v) =>
                  Array.isArray(v) && v.length > 0 ? true : 'Please select at least one language',
              }}
              render={({ field }) => (
                <MultiSelectDropdown
                  options={languages || []}
                  selectedIds={field.value || []}
                  onChange={field.onChange}
                  placeholder="Choose Language(s)"
                  // className="z-[999]"
                />
              )}
            />

            {errors.languages && (
              <p className="text-red-500 text-xs mt-1">{errors.languages.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-[#621C43]">
              Currency Type <span className="text-red-500">*</span>
            </label>
            <Controller
              name="currency"
              control={control}
              rules={{ required: 'Please select a currency' }}
              render={({ field }) => (
                <CurrencyDropdown
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value);
                    // Mark as manually modified
                    setValue('userModifiedCurrency', true, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                  placeholder="Choose Reward Currency"
                  required
                />
              )}
            />
            {errors.currency && (
              <p className="text-red-500 text-xs mt-1">{errors.currency.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-[#621C43]">
              Reward Amount (per day of survey set) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              step="5"
              min={1}
              {...register('rewardAmount', {
                valueAsNumber: true,
                required: 'Reward amount is required',
                min: {
                  value: 1,
                  message: 'Reward amount must be greater than 1',
                },
                validate: (v) => Number.isFinite(v) || 'Invalid reward amount',
              })}
              className="w-full p-2 px-3 border-[#4A082D] rounded-lg bg-[#4A082D33]"
              placeholder="Enter reward amount"
            />

            {errors.rewardAmount && (
              <p className="text-red-500 text-xs mt-1">{errors.rewardAmount.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-[#621C43]">
              Claim Reward <span className="text-red-500">*</span>
            </label>
            <Controller
              name="claimType"
              control={control}
              rules={{
                required: 'Claim Reward Type is required',
                validate: (v) =>
                  ['COMPLETE_SURVEY_SET', 'PER_SURVEY'].includes(v)
                    ? true
                    : 'Please select a valid claim reward type',
              }}
              render={({ field }) => (
                <CustomDropdown
                  value={field.value ?? ''}
                  onChange={(value) => field.onChange(value)}
                  options={[
                    { value: '', label: 'Select Claim Reward' },
                    {
                      value: 'COMPLETE_SURVEY_SET',
                      label: 'Complete Survey Set',
                    },
                    {
                      value: 'PER_SURVEY',
                      label: 'Per Survey',
                    },
                  ]}
                  placeholder="Select Claim Reward"
                  className="border-[#4A082D] rounded-lg placeholder-[#621C43] bg-[#4A082D33]/10 text-[#4A082D] font-normal italic"
                />
              )}
            />

            {/* <select
              {...register('claimType', {
                required: 'Claim Reward Type is required',
                validate: (v) =>
                  ['COMPLETE_SURVEY_SET', 'PER_SURVEY'].includes(v)
                    ? true
                    : 'Please select a valid claim reward type',
              })}
              className="w-full p-2 px-3 border-[#4A082D] rounded-lg bg-[#4A082D33] placeholder-[#621C43] text-[#4A082D] font-normal italic"
            >
              <option value="">Select Claim Reward</option>
              <option value="COMPLETE_SURVEY_SET">Complete Survey Set</option>
              <option value="PER_SURVEY">Per Survey</option>
            </select> */}

            {errors.claimType && (
              <p className="text-red-500 text-xs mt-1">{errors.claimType.message}</p>
            )}
          </div>
          <div>
            <Controller
              name="startDate"
              control={control}
              rules={{
                required: 'Start date is required',
                validate: !isEditing
                  ? {
                      notInPast: (value) => {
                        if (!value) return true;
                        const selectedDate = new Date(value);
                        const today = new Date();
                        selectedDate.setHours(0, 0, 0, 0);
                        today.setHours(0, 0, 0, 0);
                        return selectedDate >= today || 'Start date should not be in the past';
                      },
                    }
                  : undefined,
              }}
              render={({ field }) => (
                <DatePicker
                  label="Start Date"
                  required
                  value={field.value || ''}
                  onChange={field.onChange}
                  placeholder="Select start date"
                  error={errors.startDate?.message}
                  minDate={isEditing ? undefined : new Date().toISOString().split('T')[0]}
                />
              )}
            />
          </div>

          <div>
            <Controller
              name="endDate"
              control={control}
              rules={{
                required: 'End date is required',
                validate: (endDate, formValues) => validateEndDateWithSurveys(endDate, formValues),
              }}
              render={({ field }) => (
                <DatePicker
                  label="End Date"
                  required
                  value={field.value || ''}
                  onChange={field.onChange}
                  placeholder="Select end date"
                  error={errors.endDate?.message}
                  minDate={new Date().toISOString().split('T')[0]}
                />
              )}
            />
          </div>

          {/* Timezone Picker - Always show when scheduled */}
          <div className="flex flex-col">
            <label className="text-sm font-medium mb-1 text-[#621C43]">
              Timezone <span className="text-red-500">*</span>
            </label>
            <div className="h-[38px]">
              <Controller
                name="timezone"
                control={control}
                rules={{ required: 'Timezone is required' }}
                render={({ field }) => (
                  <DropdownWithRadio
                    label="Timezone"
                    options={timezoneOptions}
                    selected={field.value}
                    onChange={(value) => {
                      field.onChange(value);
                      // Mark as manually modified
                      setValue('userModifiedTimezone', true, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    }}
                    isOpen={openDropdown === 'timezone'}
                    onOpen={() => setOpenDropdown('timezone')}
                    onClose={() => setOpenDropdown(null)}
                    placeholder="Select Timezone"
                    className="h-full flex flex-row relative border border-gray-300 rounded-lg bg-[#F2E9EA] text-sm"
                  />
                )}
              />

              {errors.timezone && (
                <p className="text-red-500 text-xs mt-1">{errors.timezone.message}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  },
);

export default BasicInfo;
